import {
  CheckboxController,
  Collapse,
  GroupedController,
  NumberController,
  ReactSelectController,
  Text,
} from "@pushpress/shared-components";
import { useContext } from "react";
import { type Control, useWatch } from "react-hook-form";
import { intervalTypeOptions } from "../PlanDrawer.utils";
import TaxSelectController from "../../../../components/controllers/TaxSelect";
import { PlanType } from "../../../../apis/subscriptionApi";
import { PlanForm } from "../PlanDrawer.types";
import { getCurrencySymbol } from "../../../../utils/currencyUtils";
import { AppContext } from "../../../../context/app";

interface BillingSectionProps {
  control: Control<PlanForm>;
  planType: PlanType;
}

const BillingSection = (props: BillingSectionProps) => {
  const { control, planType } = props;

  const { client } = useContext(AppContext);

  const [recursForever, hasSetupFee] = useWatch({
    control,
    name: ["recursForever", "hasSetupFee"],
  });

  const currencySymbol = getCurrencySymbol(client.currencyIso);

  return (
    <div>
      <Text
        component="h2"
        variant="heading-5"
        weight="600"
        color="wolf-800"
        className="mt-4"
      >
        Billing
      </Text>
      <Text variant="body-md" color="wolf-600" className="mt-1">
        How much should clients pay, and when should they be billed?
      </Text>
      <NumberController
        control={control}
        name="price"
        label="Price"
        className="mt-3"
        required
        min={0}
        startText={currencySymbol}
      />
      {planType !== "session-pack" && (
        <GroupedController
          label={planType === "recurring" ? "Bill every" : "Expire plan in"}
          required
          flexItems={["0 0 92px", "1"]}
        >
          <NumberController
            control={control}
            name="billEveryCount"
            label="Value"
            min={1}
            autoComplete="off"
          />
          <ReactSelectController
            control={control}
            name="billEveryInterval"
            label="Type"
            options={intervalTypeOptions}
          />
        </GroupedController>
      )}
      {planType === "recurring" && (
        <>
          <CheckboxController
            control={control}
            name="recursForever"
            label="Recurs forever"
            className="mt-1"
          />
          <Collapse in={!recursForever}>
            <NumberController
              control={control}
              name="occurrences"
              label="Occurrences"
              className="mt-3"
              required
              min={2}
            />
          </Collapse>
        </>
      )}
      <CheckboxController
        control={control}
        name="hasSetupFee"
        label="Setup fee (one-time)"
        className="mt-3"
      />
      <Collapse in={hasSetupFee}>
        <NumberController
          control={control}
          name="setupFee"
          label="Setup fee"
          className="mt-3"
          required
          min={0}
          startText="$"
        />
      </Collapse>
      <TaxSelectController
        control={control}
        name="taxRate"
        label="Tax"
        className="mt-3 mb-1"
        isClearable
        hideDropdownIndicator
        placeholder="— Add —"
      />
    </div>
  );
};

export default BillingSection;
